import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AppLayout from '../../components/layout/AppLayout'
import Alert from '../../components/ui/Alert'
import { supabase } from '../../lib/supabase'
import { useAdminGuard } from '../../hooks/useAdminGuard'
import type { Supplier } from '../../types'

type SubTab = 'cadastro' | 'vincular'

const SUB_TABS: { key: SubTab; label: string; adminOnly?: boolean }[] = [
  { key: 'cadastro', label: 'Cadastro de Fornecedores', adminOnly: true },
  { key: 'vincular', label: 'Vincular Produto a Fornecedor' },
]

interface ProductRow {
  id: string
  sku: string
  name: string
  current_stock: number
  supplier_id: string | null
  supplier_name: string | null
}

function formatCnpj(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14)
  if (d.length <= 2)  return d
  if (d.length <= 5)  return `${d.slice(0, 2)}.${d.slice(2)}`
  if (d.length <= 8)  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`
}

function formatPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11)
  if (d.length === 0)  return ''
  if (d.length <= 2)   return `(${d}`
  if (d.length <= 6)   return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10)  return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

export default function SuppliersPage() {
  const { isAdmin, loading: loadingRole } = useAdminGuard()
  const [searchParams, setSearchParams] = useSearchParams()
  const visibleTabs = SUB_TABS.filter(t => !t.adminOnly || isAdmin)
  const requested = (searchParams.get('tab') as SubTab) || (isAdmin ? 'cadastro' : 'vincular')
  const subTab: SubTab = visibleTabs.some(t => t.key === requested)
    ? requested
    : (isAdmin ? 'cadastro' : 'vincular')
  const setSubTab = (k: SubTab) => setSearchParams({ tab: k }, { replace: true })

  useEffect(() => {
    if (loadingRole) return
    if (!isAdmin && requested === 'cadastro') {
      setSearchParams({ tab: 'vincular' }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingRole, isAdmin, requested])

  // ── Fornecedores (cadastro) ────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [supplierFilter, setSupplierFilter] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [editCnpj, setEditCnpj] = useState('')
  const [editLeadTime, setEditLeadTime] = useState(7)
  const [editMinOrder, setEditMinOrder] = useState('')
  const [editTerms, setEditTerms] = useState(['', '', '', '', '', ''])
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newCnpj, setNewCnpj] = useState('')
  const [newLeadTime, setNewLeadTime] = useState(7)
  const [newMinOrder, setNewMinOrder] = useState('')
  const [newTerms, setNewTerms] = useState(['', '', '', '', '', ''])
  const [supplierMsg, setSupplierMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)

  // ── Vincular produto a fornecedor ─────────────────────────────────────
  const [reassignSearch, setReassignSearch]   = useState('')
  const [reassignResults, setReassignResults] = useState<ProductRow[]>([])
  const [reassignLoading, setReassignLoading] = useState(false)
  const [reassignAssign, setReassignAssign]   = useState<Record<string, string>>({})
  const [reassignSaving, setReassignSaving]   = useState<string | null>(null)
  const [reassignMsg, setReassignMsg]         = useState<{ type: 'error' | 'success'; text: string } | null>(null)
  const reassignDebounceRef                    = useRef<number | null>(null)

  useEffect(() => {
    fetchSuppliers()
    fetchOrphansAsDefault()
  }, [])

  async function fetchSuppliers() {
    const { data } = await supabase.from('suppliers').select('*').order('name')
    setSuppliers((data ?? []) as Supplier[])
  }

  function mapWithSupplier(rows: any[]): ProductRow[] {
    return rows.map(p => ({
      id: p.id,
      sku: p.sku,
      name: p.name,
      current_stock: p.current_stock,
      supplier_id: p.supplier_id,
      supplier_name: suppliers.find(s => s.id === p.supplier_id)?.name ?? null,
    }))
  }

  async function fetchOrphansAsDefault() {
    setReassignLoading(true)
    const { data } = await supabase
      .from('products')
      .select('id, sku, name, current_stock, supplier_id')
      .eq('is_active', true)
      .is('supplier_id', null)
      .order('current_stock', { ascending: true })
      .limit(100)
    setReassignResults(mapWithSupplier(data ?? []))
    setReassignLoading(false)
  }

  async function searchProducts(term: string) {
    const trimmed = term.trim()
    if (trimmed.length === 0) {
      fetchOrphansAsDefault()
      return
    }
    if (trimmed.length < 2) {
      setReassignResults([])
      return
    }
    setReassignLoading(true)
    const wildcard = `%${trimmed}%`
    const [skuRes, nameRes] = await Promise.all([
      supabase.from('products').select('id, sku, name, current_stock, supplier_id')
        .eq('is_active', true).is('deleted_at', null).ilike('sku', wildcard).order('name').limit(50),
      supabase.from('products').select('id, sku, name, current_stock, supplier_id')
        .eq('is_active', true).is('deleted_at', null).ilike('name', wildcard).order('name').limit(50),
    ])
    const map = new Map<string, any>()
    ;(skuRes.data ?? []).forEach((p: any) => map.set(p.id, p))
    ;(nameRes.data ?? []).forEach((p: any) => map.set(p.id, p))
    const merged = Array.from(map.values()).slice(0, 50)
    setReassignResults(mapWithSupplier(merged))
    setReassignLoading(false)
  }

  async function saveReassign(productId: string) {
    const product = reassignResults.find(p => p.id === productId)
    if (!product) return
    const selected = reassignAssign[productId]
    const newSupplierId = selected !== undefined ? selected : (product.supplier_id ?? '')
    setReassignSaving(productId)
    setReassignMsg(null)
    const { error } = await supabase
      .from('products')
      .update({ supplier_id: newSupplierId || null })
      .eq('id', productId)
    setReassignSaving(null)
    if (error) { setReassignMsg({ type: 'error', text: error.message }); return }
    const newSupplierName = suppliers.find(s => s.id === newSupplierId)?.name ?? null
    setReassignResults(prev => prev.map(p =>
      p.id === productId
        ? { ...p, supplier_id: newSupplierId || null, supplier_name: newSupplierName }
        : p
    ))
    setReassignAssign(prev => { const n = { ...prev }; delete n[productId]; return n })
    setReassignMsg({ type: 'success', text: 'Fornecedor atualizado.' })
  }

  function fmtTerms(s: Supplier): string {
    const terms = [s.payment_term_1, s.payment_term_2, s.payment_term_3, s.payment_term_4, s.payment_term_5, s.payment_term_6]
      .filter((t): t is number => t != null)
    return terms.length ? terms.join(' / ') + 'd' : '—'
  }

  function startEdit(s: Supplier) {
    setEditingId(s.id)
    setEditName(s.name)
    setEditEmail(s.email ?? '')
    setEditPhone(s.phone ? formatPhone(s.phone) : '')
    setEditCnpj(s.cnpj ? formatCnpj(s.cnpj) : '')
    setEditLeadTime(s.lead_time_days)
    setEditMinOrder(s.min_order != null ? String(s.min_order) : '')
    setEditTerms([
      s.payment_term_1 != null ? String(s.payment_term_1) : '',
      s.payment_term_2 != null ? String(s.payment_term_2) : '',
      s.payment_term_3 != null ? String(s.payment_term_3) : '',
      s.payment_term_4 != null ? String(s.payment_term_4) : '',
      s.payment_term_5 != null ? String(s.payment_term_5) : '',
      s.payment_term_6 != null ? String(s.payment_term_6) : '',
    ])
  }

  async function saveSupplier() {
    if (!editingId) return
    const { error } = await supabase.from('suppliers').update({
      name: editName.trim(),
      email: editEmail || null,
      phone: editPhone || null,
      cnpj: editCnpj.trim() || null,
      lead_time_days: editLeadTime,
      min_order: editMinOrder ? Number(editMinOrder) : null,
      payment_term_1: editTerms[0] ? Number(editTerms[0]) : null,
      payment_term_2: editTerms[1] ? Number(editTerms[1]) : null,
      payment_term_3: editTerms[2] ? Number(editTerms[2]) : null,
      payment_term_4: editTerms[3] ? Number(editTerms[3]) : null,
      payment_term_5: editTerms[4] ? Number(editTerms[4]) : null,
      payment_term_6: editTerms[5] ? Number(editTerms[5]) : null,
    }).eq('id', editingId)
    if (error) {
      setSupplierMsg({ type: 'error', text: error.message })
      return
    }
    setEditingId(null)
    setSupplierMsg({ type: 'success', text: 'Fornecedor atualizado.' })
    fetchSuppliers()
  }

  async function createSupplier() {
    if (!newName.trim()) {
      setSupplierMsg({ type: 'error', text: 'Nome é obrigatório.' })
      return
    }
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
    if (!profile) return

    const { error } = await supabase.from('suppliers').insert({
      company_id: profile.company_id,
      name: newName.trim(),
      email: newEmail || null,
      phone: newPhone || null,
      cnpj: newCnpj.trim() || null,
      lead_time_days: newLeadTime,
      min_order: newMinOrder ? Number(newMinOrder) : null,
      payment_term_1: newTerms[0] ? Number(newTerms[0]) : null,
      payment_term_2: newTerms[1] ? Number(newTerms[1]) : null,
      payment_term_3: newTerms[2] ? Number(newTerms[2]) : null,
      payment_term_4: newTerms[3] ? Number(newTerms[3]) : null,
      payment_term_5: newTerms[4] ? Number(newTerms[4]) : null,
      payment_term_6: newTerms[5] ? Number(newTerms[5]) : null,
    })

    if (error) {
      setSupplierMsg({ type: 'error', text: error.message })
      return
    }
    setNewName(''); setNewEmail(''); setNewPhone(''); setNewCnpj(''); setNewLeadTime(7); setNewMinOrder(''); setNewTerms(['', '', '', '', '', ''])
    setCreating(false)
    setSupplierMsg({ type: 'success', text: 'Fornecedor criado.' })
    fetchSuppliers()
  }

  async function deleteSupplier(id: string, name: string) {
    if (!confirm(`Remover fornecedor "${name}"?`)) return
    const { error } = await supabase.from('suppliers').delete().eq('id', id)
    if (error) {
      setSupplierMsg({ type: 'error', text: error.message })
      return
    }
    setSupplierMsg({ type: 'success', text: 'Fornecedor removido.' })
    fetchSuppliers()
  }

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(supplierFilter.toLowerCase())
  )

  return (
    <AppLayout title="Fornecedor">
      <div className="w-full">
        {/* Sub-abas — desktop: linha com border-bottom; mobile: chips grandes em grid */}
        <div className="hidden md:flex gap-1 border-b border-line mb-6 overflow-x-auto">
          {visibleTabs.map(t => (
            <button
              key={t.key}
              onClick={() => setSubTab(t.key)}
              className={`text-sm sm:text-base px-3 sm:px-5 py-2.5 border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                subTab === t.key
                  ? 'border-brand-500 text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="md:hidden grid gap-2 mb-4" style={{ gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))` }}>
          {visibleTabs.map(t => (
            <button
              key={t.key}
              onClick={() => setSubTab(t.key)}
              className={`h-11 rounded-2xl text-xs font-bold leading-tight px-2 touch-action-manip transition-colors ${
                subTab === t.key
                  ? 'bg-brand-600 text-white ring-2 ring-ink/50'
                  : 'bg-sunken text-ink-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {subTab === 'cadastro' && (
        <section className="bg-surface border border-line rounded-xl overflow-hidden">
          <div className="p-6 border-b border-line flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-ink mb-1">Fornecedores</h2>
              <p className="text-sm text-ink-faint">
                {suppliers.length} cadastrados · E-mail e telefone não vêm do ERP — cadastre aqui para enviar pedidos.
              </p>
            </div>
            <button
              onClick={() => { setCreating(true); setSupplierMsg(null) }}
              className="shrink-0 px-4 py-2 bg-base-600 hover:bg-base-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              + Novo Fornecedor
            </button>
          </div>

          {supplierMsg && (
            <div className="px-6 pt-4">
              <Alert type={supplierMsg.type} message={supplierMsg.text} />
            </div>
          )}

          {creating && (
            <div className="px-6 py-4 border-b border-line bg-sunken">
              <h3 className="text-sm font-semibold text-ink mb-3">Novo Fornecedor</h3>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nome"
                  className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@fornecedor.com"
                  className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                <input value={newPhone} onChange={e => setNewPhone(formatPhone(e.target.value))} placeholder="(11) 98401-6741" maxLength={15}
                  className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                <input value={newCnpj} onChange={e => setNewCnpj(formatCnpj(e.target.value))} placeholder="00.000.000/0000-00" maxLength={18}
                  className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm font-mono" />
                <input type="number" value={newLeadTime} onChange={e => setNewLeadTime(Number(e.target.value))}
                  placeholder="Lead time (dias)"
                  className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                <input type="number" value={newMinOrder} onChange={e => setNewMinOrder(e.target.value)}
                  placeholder="Compra mín. R$"
                  className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
              </div>
              <div className="mt-3">
                <p className="text-xs text-ink-faint mb-1.5">Prazos de pagamento (dias) — preencha os que se aplicam</p>
                <div className="flex flex-wrap gap-2">
                  {newTerms.map((v, i) => (
                    <input
                      key={i}
                      type="number"
                      value={v}
                      onChange={e => setNewTerms(prev => { const n = [...prev]; n[i] = e.target.value; return n })}
                      placeholder={`Prazo ${i + 1}`}
                      className="w-24 bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm"
                    />
                  ))}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={createSupplier} className="text-xs px-3 py-1.5 bg-base-600 hover:bg-base-500 rounded text-white">Criar</button>
                <button onClick={() => { setCreating(false); setNewName(''); setNewEmail(''); setNewPhone(''); setNewCnpj(''); setNewLeadTime(7); setNewMinOrder(''); setNewTerms(['', '', '', '', '', '']) }}
                  className="text-xs px-3 py-1.5 bg-sunken hover:bg-line rounded text-ink-muted">Cancelar</button>
              </div>
            </div>
          )}

          <div className="px-6 py-3 border-b border-line bg-surface">
            <input
              type="search"
              value={supplierFilter}
              onChange={e => setSupplierFilter(e.target.value)}
              placeholder="Buscar fornecedor..."
              className="w-full md:w-80 bg-paper border border-line rounded px-3 py-1.5 text-ink text-sm placeholder:text-ink-faint"
            />
            {supplierFilter && (
              <span className="ml-3 text-xs text-ink-faint">{filteredSuppliers.length} resultados</span>
            )}
          </div>

          <div className="overflow-y-auto max-h-[600px]">
            <table className="w-full text-sm mobile-stack-table">
              <thead className="bg-sunken border-b border-line sticky top-0">
                <tr>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Nome</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">E-mail</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Telefone</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2 whitespace-nowrap">CNPJ</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Lead Time</th>
                  <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Compra Mín.</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Prazos (dias)</th>
                  <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map(s => (
                  <tr key={s.id} className="border-b border-line hover:bg-sunken">
                    {editingId === s.id ? (
                      <td colSpan={8} className="px-4 py-4 bg-sunken">
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                            <div className="md:col-span-2">
                              <label className="block text-[10px] text-ink-faint uppercase mb-1">Nome</label>
                              <input value={editName} onChange={e => setEditName(e.target.value)}
                                className="w-full bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-faint uppercase mb-1">CNPJ</label>
                              <input value={editCnpj} onChange={e => setEditCnpj(formatCnpj(e.target.value))}
                                placeholder="00.000.000/0000-00" maxLength={18}
                                className="w-full bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm font-mono" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-faint uppercase mb-1">E-mail</label>
                              <input value={editEmail} onChange={e => setEditEmail(e.target.value)}
                                className="w-full bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-faint uppercase mb-1">Telefone</label>
                              <input value={editPhone} onChange={e => setEditPhone(formatPhone(e.target.value))}
                                placeholder="(11) 98401-6741" maxLength={15}
                                className="w-full bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-faint uppercase mb-1">Lead time</label>
                              <input type="number" value={editLeadTime} onChange={e => setEditLeadTime(Number(e.target.value))}
                                className="w-full bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                            </div>
                            <div>
                              <label className="block text-[10px] text-ink-faint uppercase mb-1">Compra mín.</label>
                              <input type="number" value={editMinOrder} onChange={e => setEditMinOrder(e.target.value)}
                                placeholder="R$"
                                className="w-full bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm" />
                            </div>
                          </div>

                          <div>
                            <label className="block text-[10px] text-ink-faint uppercase mb-1">Prazos de pagamento (dias)</label>
                            <div className="flex flex-wrap gap-2">
                              {editTerms.map((v, i) => (
                                <input
                                  key={i}
                                  type="number"
                                  value={v}
                                  onChange={e => setEditTerms(prev => { const n = [...prev]; n[i] = e.target.value; return n })}
                                  placeholder={`Prazo ${i + 1}`}
                                  className="w-24 bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm"
                                />
                              ))}
                            </div>
                          </div>

                          <div className="flex gap-2 pt-1">
                            <button onClick={saveSupplier} className="text-xs px-4 py-1.5 bg-base-600 hover:bg-base-500 rounded text-white">Salvar</button>
                            <button onClick={() => setEditingId(null)} className="text-xs px-4 py-1.5 bg-sunken hover:bg-line rounded text-ink-muted">Cancelar</button>
                          </div>
                        </div>
                      </td>
                    ) : (
                      <>
                        <td className="px-4 py-2.5 text-ink">{s.name}</td>
                        <td className="px-4 py-2.5 text-ink-muted">{s.email ?? '—'}</td>
                        <td className="px-4 py-2.5 text-ink-muted whitespace-nowrap">{s.phone ? formatPhone(s.phone) : '—'}</td>
                        <td className="px-4 py-2.5 text-ink-muted font-mono text-xs whitespace-nowrap">{s.cnpj ? formatCnpj(s.cnpj) : '—'}</td>
                        <td className="px-4 py-2.5 text-ink-muted">{s.lead_time_days} dias</td>
                        <td className="px-4 py-2.5 text-right text-ink-muted">
                          {s.min_order != null
                            ? s.min_order.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-ink-muted text-sm">{fmtTerms(s)}</td>
                        <td className="px-4 py-2.5 text-right space-x-2 whitespace-nowrap">
                          <button onClick={() => startEdit(s)} className="text-xs px-3 py-1 bg-sunken hover:bg-line rounded text-ink-muted">Editar</button>
                          <button onClick={() => deleteSupplier(s.id, s.name)} className="text-xs px-3 py-1 bg-danger/10 hover:bg-danger/20 border border-danger/30 rounded text-danger">Remover</button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                {filteredSuppliers.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-faint">
                    {supplierFilter ? 'Nenhum fornecedor encontrado.' : 'Nenhum fornecedor. Sincronize com o ERP primeiro.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        )}

        {subTab === 'vincular' && (
        <section className="bg-surface border border-line rounded-xl overflow-hidden">
          <div className="p-6 border-b border-line">
            <h2 className="text-lg font-semibold text-ink mb-1">Vincular Produto a Fornecedor</h2>
            <p className="text-sm text-ink-faint">
              Digite o código (SKU) ou descrição do produto para buscar e alterar o fornecedor vinculado.
              Sem busca, exibimos os produtos sem fornecedor cadastrado.
            </p>
          </div>

          {reassignMsg && (
            <div className="px-6 pt-4">
              <Alert type={reassignMsg.type} message={reassignMsg.text} />
            </div>
          )}

          <div className="px-6 py-3 border-b border-line bg-surface">
            <input
              type="search"
              value={reassignSearch}
              onChange={e => {
                const v = e.target.value
                setReassignSearch(v)
                if (reassignDebounceRef.current) window.clearTimeout(reassignDebounceRef.current)
                reassignDebounceRef.current = window.setTimeout(() => searchProducts(v), 350)
              }}
              placeholder="Buscar por código (SKU) ou descrição..."
              className="w-full bg-paper border border-line rounded px-3 py-1.5 text-ink text-sm placeholder:text-ink-faint"
            />
            <p className="mt-2 text-xs text-ink-faint">
              {reassignLoading
                ? 'Buscando...'
                : reassignSearch.trim().length === 0
                  ? `${reassignResults.length} produto(s) sem fornecedor`
                  : `${reassignResults.length} resultado(s)`}
            </p>
          </div>

          <div className="overflow-y-auto max-h-[560px]">
            <table className="w-full text-sm mobile-stack-table">
              <thead className="bg-sunken border-b border-line sticky top-0">
                <tr>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">SKU</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Produto</th>
                  <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Estoque</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Fornecedor atual</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Novo fornecedor</th>
                  <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {reassignResults.map(p => {
                  const selected = reassignAssign[p.id]
                  const currentValue = selected !== undefined ? selected : (p.supplier_id ?? '')
                  const changed = selected !== undefined && selected !== (p.supplier_id ?? '')
                  return (
                    <tr key={p.id} className="border-b border-line hover:bg-sunken">
                      <td className="px-4 py-2 font-mono text-xs text-ink-muted">{p.sku}</td>
                      <td className="px-4 py-2 text-ink">{p.name}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${p.current_stock <= 0 ? 'text-danger font-semibold' : 'text-ink-muted'}`}>
                        {p.current_stock}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {p.supplier_name
                          ? <span className="text-ink-muted">{p.supplier_name}</span>
                          : <span className="text-ink-faint italic">Sem fornecedor</span>}
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={currentValue}
                          onChange={e => setReassignAssign(prev => ({ ...prev, [p.id]: e.target.value }))}
                          className="w-full bg-paper border border-line rounded px-2 py-1 text-ink text-sm"
                        >
                          <option value="">Sem fornecedor</option>
                          {suppliers.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => saveReassign(p.id)}
                          disabled={!changed || reassignSaving === p.id}
                          className="text-xs px-3 py-1 bg-base-600 hover:bg-base-500 disabled:opacity-40 disabled:cursor-not-allowed rounded text-white"
                        >
                          {reassignSaving === p.id ? 'Salvando...' : 'Salvar'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
                {!reassignLoading && reassignResults.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-faint">
                    {reassignSearch.trim().length === 0
                      ? 'Todos os produtos ativos têm fornecedor vinculado.'
                      : 'Nenhum produto encontrado.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        )}
      </div>
    </AppLayout>
  )
}
